import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from '../../i18n';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import { getSoundsEnabled, setSoundsEnabled, getVolume, setVolume } from '../../utils/sounds';
import { AppIcon } from './icons';
import type { RoomBranding, RoomSettings, CompanyQuestion, AiPromptUsage } from '../../types/game';
import {
  BRANDING_MAX_COLORS,
  buildBrandingSavePayload,
  fileToBrandingBackgroundDataUrl,
  fileToBrandingLogoDataUrl,
} from '../../utils/roomBranding';
import {
  parseCompanyQuestionsEdit,
  serializeCompanyQuestionsForEdit,
  companyQuestionsEditErrorKey,
} from '../../utils/companyQuestionsEdit';
import './SettingsModal.css';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  socket: WebSocketAdapter;
  roomName: string;
  currentSettings?: RoomSettings;
  currentBranding?: RoomBranding | null;
  companyQuestions?: CompanyQuestion[];
  aiPromptUsage?: AiPromptUsage | null;
  isPremium?: boolean;
  /** Open premium / pricing flow (non-premium branding lock). */
  onGoPremium?: () => void;
  /** Same as root “Have a code?” — enter an existing premium/admin room. */
  onHavePremiumCode?: () => void;
}

const TIMER_VALUES = [15000, 20000, 30000, 45000];
const QUESTION_COUNT_VALUES = [5, 10, 15, 20, 25, 30];
const DEFAULT_NUMBER_OF_QUESTIONS = 20;
const MAX_COLORS = BRANDING_MAX_COLORS;
const DEFAULT_SWATCH = '#EF4444';
/** Keep in sync with be AI_MAX_STORED_QUESTIONS. */
const AI_MAX_COMPANY_QUESTIONS = 100;

type ColorSwatch = { id: string; value: string };

let colorSwatchSeq = 0;
function makeColorSwatchId(): string {
  colorSwatchSeq += 1;
  return `swatch-${colorSwatchSeq}`;
}

function toColorSwatches(colors: string[] | undefined): ColorSwatch[] {
  return (colors ?? []).slice(0, MAX_COLORS).map((raw) => {
    const value = /^#[0-9A-Fa-f]{6}$/.test(raw) ? raw.toUpperCase() : DEFAULT_SWATCH;
    return { id: makeColorSwatchId(), value };
  });
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  socket,
  roomName,
  currentSettings,
  currentBranding,
  companyQuestions: companyQuestionsProp,
  aiPromptUsage: aiPromptUsageProp,
  isPremium = false,
  onGoPremium,
  onHavePremiumCode,
}) => {
  const { t } = useTranslation();
  const brandingEditable = isPremium;
  const logoInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  /** While native color picker is open, ignore overlay clicks (picker UI lives outside the modal). */
  const colorPickerOpenRef = useRef(false);
  /** Live color values — updated during picker use without setState (avoids closing Chromium picker). */
  const colorValuesRef = useRef<Record<string, string>>({});
  const [settings, setSettings] = useState<RoomSettings>({
    timerDuration: currentSettings?.timerDuration ?? 20000,
    numberOfQuestions: currentSettings?.numberOfQuestions ?? DEFAULT_NUMBER_OF_QUESTIONS,
  });
  const [displayName, setDisplayName] = useState(currentBranding?.displayName ?? '');
  const [colorSwatches, setColorSwatches] = useState<ColorSwatch[]>(() => {
    const swatches = toColorSwatches(currentBranding?.colors);
    colorValuesRef.current = Object.fromEntries(swatches.map((s) => [s.id, s.value]));
    return swatches;
  });
  const [website, setWebsite] = useState(currentBranding?.website ?? '');
  const [allowWebsiteCrawl, setAllowWebsiteCrawl] = useState(
    currentBranding?.allowWebsiteCrawl ?? false
  );
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(currentBranding?.logoDataUrl);
  const [backgroundDataUrl, setBackgroundDataUrl] = useState<string | undefined>(
    currentBranding?.backgroundDataUrl
  );
  const [logoError, setLogoError] = useState<string | null>(null);
  const [backgroundError, setBackgroundError] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [backgroundBusy, setBackgroundBusy] = useState(false);
  const [audioOn, setAudioOn] = useState(() => getSoundsEnabled());
  const [volume, setVolumeState] = useState(() => getVolume());
  const [companyQuestions, setCompanyQuestions] = useState<CompanyQuestion[]>(
    companyQuestionsProp ?? []
  );
  const [aiPromptUsage, setAiPromptUsage] = useState<AiPromptUsage>(
    aiPromptUsageProp ?? { used: 0, limit: 3 }
  );
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showQuestionsPreview, setShowQuestionsPreview] = useState(true);
  const [questionsFullscreen, setQuestionsFullscreen] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [topUpBusy, setTopUpBusy] = useState(false);
  const [customQuestion, setCustomQuestion] = useState('');
  const [customOptions, setCustomOptions] = useState(['', '', '', '']);
  const [customCorrectIndex, setCustomCorrectIndex] = useState(0);
  const [customInteresting, setCustomInteresting] = useState('');
  const [customBusy, setCustomBusy] = useState(false);
  const [showCustomQuestionForm, setShowCustomQuestionForm] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [questionsEditText, setQuestionsEditText] = useState('');
  const [questionsEditBusy, setQuestionsEditBusy] = useState(false);
  const [questionsEditDirty, setQuestionsEditDirty] = useState(false);

  const timerOptions = useMemo(
    () =>
      TIMER_VALUES.map((value) => ({
        value,
        label: t(`lobby.settings.timerOptions.${value / 1000}`),
      })),
    [t]
  );

  const questionOptions = useMemo(
    () =>
      QUESTION_COUNT_VALUES.map((value) => ({
        value,
        label: t(`lobby.settings.questionOptions.${value}`),
      })),
    [t]
  );

  useEffect(() => {
    if (currentSettings) {
      setSettings({
        timerDuration: currentSettings.timerDuration ?? 20000,
        numberOfQuestions: currentSettings.numberOfQuestions ?? DEFAULT_NUMBER_OF_QUESTIONS,
      });
    }
  }, [currentSettings]);

  const replaceColorSwatches = (next: ColorSwatch[]) => {
    colorValuesRef.current = Object.fromEntries(next.map((s) => [s.id, s.value]));
    setColorSwatches(next);
  };

  const commitColorSwatchesFromRefs = () => {
    setColorSwatches((prev) =>
      prev.map((s) => ({
        ...s,
        value: (colorValuesRef.current[s.id] ?? s.value).toUpperCase(),
      }))
    );
  };

  const getColorsForSave = () =>
    colorSwatches.map((s) => (colorValuesRef.current[s.id] ?? s.value).toUpperCase());

  // Do NOT sync branding from props while the modal is open.
  // room-state / focus churn (e.g. native file picker) replaces `currentBranding` and
  // would wipe unsaved logo/background — then Save clears the other image on the server.
  useEffect(() => {
    if (isOpen) return;
    setDisplayName(currentBranding?.displayName ?? '');
    replaceColorSwatches(toColorSwatches(currentBranding?.colors));
    setWebsite(currentBranding?.website ?? '');
    setAllowWebsiteCrawl(currentBranding?.allowWebsiteCrawl ?? false);
    setLogoDataUrl(currentBranding?.logoDataUrl);
    setBackgroundDataUrl(currentBranding?.backgroundDataUrl);
    setLogoError(null);
    setBackgroundError(null);
  }, [currentBranding, isOpen]);

  useEffect(() => {
    setCompanyQuestions((prev) => {
      const next = companyQuestionsProp ?? [];
      // Public room-state strips correctAnswer; don't wipe a host-full bank we already have.
      if (
        next.length > 0 &&
        prev.length > 0 &&
        !next.some((q) => !!q.correctAnswer) &&
        prev.some((q) => !!q.correctAnswer)
      ) {
        return prev;
      }
      return next;
    });
  }, [companyQuestionsProp]);

  useEffect(() => {
    if (aiPromptUsageProp) {
      setAiPromptUsage(aiPromptUsageProp);
    }
  }, [aiPromptUsageProp]);

  useEffect(() => {
    if (!isOpen || !brandingEditable) return;

    const onGenerating = () => {
      setAiGenerating(true);
      setAiError(null);
    };
    const preferHostQuestions = (
      incoming: CompanyQuestion[] | undefined,
      prev: CompanyQuestion[]
    ): CompanyQuestion[] => {
      if (!incoming) return prev;
      const incomingHasAnswers = incoming.some((q) => !!q.correctAnswer);
      const prevHasAnswers = prev.some((q) => !!q.correctAnswer);
      // Room broadcast of company-questions-generated is answer-stripped; keep host bank.
      if (!incomingHasAnswers && prevHasAnswers) return prev;
      return incoming;
    };
    const onGenerated = (data: {
      questions?: CompanyQuestion[];
      aiPromptUsage?: AiPromptUsage;
      customAdded?: boolean;
      updated?: boolean;
    }) => {
      setAiGenerating(false);
      setCustomBusy(false);
      setQuestionsEditBusy(false);
      if (data.questions) {
        setCompanyQuestions((prev) => preferHostQuestions(data.questions, prev));
      }
      if (data.aiPromptUsage) setAiPromptUsage(data.aiPromptUsage);
      setShowQuestionsPreview(true);
      setEditingQuestions(false);
      setQuestionsEditText('');
      setQuestionsEditDirty(false);
      if (data.customAdded) {
        setCustomQuestion('');
        setCustomOptions(['', '', '', '']);
        setCustomCorrectIndex(0);
        setCustomInteresting('');
      }
    };
    const onHostQuestions = (data: { questions?: CompanyQuestion[] }) => {
      if (Array.isArray(data.questions)) {
        setCompanyQuestions(data.questions);
      }
    };
    const onError = (data: {
      message?: string;
      code?: string;
      aiPromptUsage?: AiPromptUsage;
    }) => {
      setAiGenerating(false);
      setCustomBusy(false);
      setQuestionsEditBusy(false);
      setAiError(data.message || t('lobby.settings.companyQuestions.errorGeneric'));
      if (data.aiPromptUsage) setAiPromptUsage(data.aiPromptUsage);
    };
    const onEmailed = (data: { email?: string }) => {
      setEmailBusy(false);
      setEmailSentTo(data.email || emailInput);
      setShowEmailForm(false);
    };

    socket.on('company-questions-generating', onGenerating);
    socket.on('company-questions-generated', onGenerated);
    socket.on('company-questions-host', onHostQuestions);
    socket.on('company-questions-error', onError);
    socket.on('company-questions-emailed', onEmailed);
    return () => {
      socket.off('company-questions-generating', onGenerating);
      socket.off('company-questions-generated', onGenerated);
      socket.off('company-questions-host', onHostQuestions);
      socket.off('company-questions-error', onError);
      socket.off('company-questions-emailed', onEmailed);
    };
  }, [isOpen, brandingEditable, socket, t, emailInput]);

  useEffect(() => {
    if (!isOpen) {
      setQuestionsFullscreen(false);
      setEditingQuestions(false);
      setQuestionsEditText('');
      setQuestionsEditBusy(false);
      setQuestionsEditDirty(false);
      return;
    }
    setAudioOn(getSoundsEnabled());
    setVolumeState(getVolume());
    setAiError(null);
    setEmailSentTo(null);
    setShowCustomQuestionForm(false);
    // Hydrate once when opening from latest room branding; keep local edits until close/save
    setDisplayName(currentBranding?.displayName ?? '');
    replaceColorSwatches(toColorSwatches(currentBranding?.colors));
    setWebsite(currentBranding?.website ?? '');
    setAllowWebsiteCrawl(currentBranding?.allowWebsiteCrawl ?? false);
    setLogoDataUrl(currentBranding?.logoDataUrl);
    setBackgroundDataUrl(currentBranding?.backgroundDataUrl);
    setLogoError(null);
    setBackgroundError(null);
    setCompanyQuestions((prev) => {
      const next = companyQuestionsProp ?? [];
      if (
        next.length > 0 &&
        prev.length > 0 &&
        !next.some((q) => !!q.correctAnswer) &&
        prev.some((q) => !!q.correctAnswer)
      ) {
        return prev;
      }
      return next;
    });
    if (aiPromptUsageProp) {
      setAiPromptUsage(aiPromptUsageProp);
    }
    // Refresh host-only full bank (public room-state strips answers).
    if (brandingEditable) {
      socket.emit('get-room-state', { roomName });
    }
    // Intentionally only when isOpen flips true — avoid wiping in-progress edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!questionsFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setQuestionsFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [questionsFullscreen]);

  // If edit opened before host answers arrived, refresh pristine textarea once they do.
  useEffect(() => {
    if (!editingQuestions || questionsEditDirty) return;
    if (!companyQuestions.some((q) => !!q.correctAnswer)) return;
    const current = parseCompanyQuestionsEdit(questionsEditText, companyQuestions);
    if (current.ok) return;
    setQuestionsEditText(serializeCompanyQuestionsForEdit(companyQuestions));
  }, [companyQuestions, editingQuestions, questionsEditDirty, questionsEditText]);

  const remainingPrompts = Math.max(0, aiPromptUsage.limit - aiPromptUsage.used);
  const hasQuestions = companyQuestions.length > 0;
  const outOfPrompts = remainingPrompts <= 0;
  const atQuestionCap = companyQuestions.length >= AI_MAX_COMPANY_QUESTIONS;
  const canGenerateMore = hasQuestions && !atQuestionCap;
  /** Disable Generate when name or website is missing. */
  const canGenerateAi = !!displayName.trim() && !!website.trim();

  const questionsEditValidation = useMemo(() => {
    if (!editingQuestions) return { ok: true as const, questions: companyQuestions };
    return parseCompanyQuestionsEdit(questionsEditText, companyQuestions);
  }, [editingQuestions, questionsEditText, companyQuestions]);

  const questionsEditFormatError =
    editingQuestions && questionsEditDirty && !questionsEditValidation.ok
      ? (() => {
          const { key, n } = companyQuestionsEditErrorKey(questionsEditValidation);
          return t(key, { n });
        })()
      : null;

  const renderQuestionsList = () =>
    companyQuestions.map((q, i) => (
      <li key={q.id}>
        <strong>
          {i + 1}. {q.question}
        </strong>
        <ul>
          {q.options.map((opt) => (
            <li key={opt} className={opt === q.correctAnswer ? 'is-correct' : ''}>
              {opt}
              {opt === q.correctAnswer ? ' ✓' : ''}
            </li>
          ))}
        </ul>
        {q.interesting && <em>{q.interesting}</em>}
      </li>
    ));

  const normalizeWebsiteForSave = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleGenerateQuestions = () => {
    if (!brandingEditable || aiGenerating) return;
    const name = displayName.trim();
    if (!name) {
      setAiError(t('lobby.settings.companyQuestions.nameRequired'));
      return;
    }
    if (!website.trim()) {
      setAiError(t('lobby.settings.companyQuestions.websiteRequired'));
      return;
    }
    if (atQuestionCap) {
      setAiError(t('lobby.settings.companyQuestions.questionLimit', { max: AI_MAX_COMPANY_QUESTIONS }));
      return;
    }
    if (outOfPrompts) {
      setAiError(t('lobby.settings.companyQuestions.noCredits'));
      return;
    }
    setAiError(null);
    setAiGenerating(true);
    // Persist name/website first so crawl consent + fields stick
    socket.emit('update-room-settings', {
      roomName,
      branding: buildBrandingSavePayload({
        displayName,
        colors: getColorsForSave(),
        website,
        allowWebsiteCrawl,
        logoDataUrl,
        backgroundDataUrl,
      }),
    });
    // Only send/use website when crawl is allowed — otherwise GPT uses name only.
    const websiteForAi =
      allowWebsiteCrawl ? normalizeWebsiteForSave(website) : undefined;
    socket.emit('generate-company-questions', {
      roomName,
      displayName: name,
      website: websiteForAi,
      allowWebsiteCrawl: !!websiteForAi,
      append: hasQuestions,
    });
  };

  const handleAddCustomQuestion = () => {
    if (!brandingEditable || customBusy) return;
    const q = customQuestion.trim();
    const opts = customOptions.map((o) => o.trim());
    if (!q) {
      setAiError(t('lobby.settings.companyQuestions.customQuestionRequired'));
      return;
    }
    if (opts.some((o) => !o)) {
      setAiError(t('lobby.settings.companyQuestions.customOptionsRequired'));
      return;
    }
    setAiError(null);
    setCustomBusy(true);
    const interesting = customInteresting.trim();
    socket.emit('add-custom-company-question', {
      roomName,
      question: q,
      options: opts,
      correctIndex: customCorrectIndex,
      ...(interesting ? { interesting } : {}),
    });
  };

  const handleEmailQuestions = () => {
    if (!brandingEditable || emailBusy) return;
    const email = emailInput.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAiError(t('lobby.settings.companyQuestions.invalidEmail'));
      return;
    }
    setEmailBusy(true);
    setAiError(null);
    socket.emit('email-company-questions', { roomName, email });
  };

  const startEditingQuestions = () => {
    if (!brandingEditable || !hasQuestions) return;
    setAiError(null);
    setQuestionsFullscreen(false);
    setShowQuestionsPreview(true);
    setQuestionsEditText(serializeCompanyQuestionsForEdit(companyQuestions));
    setQuestionsEditDirty(false);
    setEditingQuestions(true);
  };

  const cancelEditingQuestions = () => {
    setEditingQuestions(false);
    setQuestionsEditText('');
    setQuestionsEditBusy(false);
    setQuestionsEditDirty(false);
    setAiError(null);
  };

  const handleSaveEditedQuestions = () => {
    if (!brandingEditable || questionsEditBusy) return;
    const parsed = parseCompanyQuestionsEdit(questionsEditText, companyQuestions);
    if (!parsed.ok) {
      const { key, n } = companyQuestionsEditErrorKey(parsed);
      setAiError(t(key, { n }));
      return;
    }
    if (parsed.questions.length > AI_MAX_COMPANY_QUESTIONS) {
      setAiError(
        t('lobby.settings.companyQuestions.questionLimit', { max: AI_MAX_COMPANY_QUESTIONS })
      );
      return;
    }
    setAiError(null);
    setQuestionsEditBusy(true);
    socket.emit('update-company-questions', {
      roomName,
      questions: parsed.questions,
    });
  };

  const handleBuyMorePrompts = async () => {
    if (!brandingEditable || topUpBusy) return;
    setTopUpBusy(true);
    setAiError(null);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageId: 'ai_prompt_topup',
          roomName,
          successUrl: `${window.location.origin}/?host=${encodeURIComponent(roomName)}&ai_topup=1&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/?host=${encodeURIComponent(roomName)}&ai_topup_canceled=1`,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.url) {
        throw new Error(data.error || t('lobby.settings.companyQuestions.topUpError'));
      }
      window.location.href = data.url;
    } catch (err) {
      setTopUpBusy(false);
      const message = err instanceof Error ? err.message : '';
      const isNetwork =
        err instanceof TypeError ||
        /failed to fetch|networkerror|load failed/i.test(message);
      setAiError(
        isNetwork
          ? t('lobby.settings.companyQuestions.topUpNetworkError')
          : message || t('lobby.settings.companyQuestions.topUpError')
      );
    }
  };

  if (!isOpen) return null;

  const handleSave = () => {
    socket.emit('update-room-settings', {
      roomName,
      settings: {
        timerDuration: settings.timerDuration,
        numberOfQuestions: settings.numberOfQuestions,
      },
      ...(brandingEditable
        ? {
            branding: buildBrandingSavePayload({
              displayName,
              colors: getColorsForSave(),
              website,
              allowWebsiteCrawl,
              logoDataUrl,
              backgroundDataUrl,
            }),
          }
        : {}),
    });
    onClose();
  };

  const handleGoPremium = () => {
    onClose();
    onGoPremium?.();
  };

  const handleHavePremiumCode = () => {
    onClose();
    onHavePremiumCode?.();
  };

  const handleLogoPick = async (file: File | null) => {
    if (!brandingEditable || !file) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const dataUrl = await fileToBrandingLogoDataUrl(file);
      setLogoDataUrl(dataUrl);
    } catch {
      setLogoError(t('lobby.settings.branding.logoError'));
    } finally {
      setLogoBusy(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleBackgroundPick = async (file: File | null) => {
    if (!brandingEditable || !file) return;
    setBackgroundBusy(true);
    setBackgroundError(null);
    try {
      const dataUrl = await fileToBrandingBackgroundDataUrl(file);
      setBackgroundDataUrl(dataUrl);
    } catch {
      setBackgroundError(t('lobby.settings.branding.backgroundError'));
    } finally {
      setBackgroundBusy(false);
      if (backgroundInputRef.current) backgroundInputRef.current.value = '';
    }
  };

  return (
    <>
    <div
      className="settings-modal-overlay"
      onClick={() => {
        // Native color picker UI is often outside `.settings-modal`; don't treat those clicks as dismiss.
        if (colorPickerOpenRef.current) return;
        onClose();
      }}
    >
      <div className="settings-modal" onClick={(e) => e.stopPropagation()} data-testid="settings-modal">
        <h2 className="settings-modal__title">
          <AppIcon name="settings" /> {t('lobby.settings.title')}
        </h2>

        <section className="settings-modal__section" data-testid="settings-game-section">
          <h3 className="settings-modal__section-title">
            {t('lobby.settings.gameSection')}
          </h3>

          <div className="settings-modal__field">
            <label className="settings-modal__label" htmlFor="settings-timer">
              {t('lobby.settings.timerDuration')}
            </label>
            <select
              id="settings-timer"
              className="settings-modal__select"
              value={settings.timerDuration ?? 20000}
              onChange={(e) => setSettings({ ...settings, timerDuration: parseInt(e.target.value) })}
            >
              {timerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="settings-modal__field">
            <label className="settings-modal__label" htmlFor="settings-questions">
              {t('lobby.settings.numberOfQuestions')}
            </label>
            <select
              id="settings-questions"
              data-testid="settings-number-of-questions"
              className="settings-modal__select"
              value={settings.numberOfQuestions ?? DEFAULT_NUMBER_OF_QUESTIONS}
              onChange={(e) => setSettings({ ...settings, numberOfQuestions: parseInt(e.target.value) })}
            >
              {questionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="settings-modal__field settings-modal__field--last">
            <label className="settings-modal__label">
              <AppIcon name="volumeUp" fontSize="small" /> {t('lobby.settings.sound')}
            </label>
            <label className="settings-modal__row">
              <span>{t('lobby.settings.soundEffects')}</span>
              <button
                type="button"
                role="switch"
                aria-checked={audioOn}
                data-testid="settings-audio-toggle"
                className={`settings-modal__switch${audioOn ? ' is-on' : ''}`}
                onClick={() => {
                  const next = !audioOn;
                  setAudioOn(next);
                  setSoundsEnabled(next);
                }}
              >
                <span className="settings-modal__switch-thumb" />
              </button>
            </label>
            <label className="settings-modal__label settings-modal__label--sub" htmlFor="settings-volume">
              {t('lobby.settings.volume')}
            </label>
            <div className="settings-modal__volume">
              <input
                id="settings-volume"
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={volume}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isNaN(v)) {
                    const clamped = Math.max(0, Math.min(1, v));
                    setVolumeState(clamped);
                    setVolume(clamped);
                  }
                }}
                data-testid="settings-volume-slider"
              />
              <span>{Math.round(volume * 100)}%</span>
            </div>
          </div>
        </section>

        <section
          className={`settings-modal__section settings-modal__section--premium${
            brandingEditable ? '' : ' is-locked'
          }`}
          data-testid="settings-branding-section"
          aria-disabled={!brandingEditable}
        >
          <h3 className="settings-modal__section-title settings-modal__section-title--premium">
            <AppIcon name="premium" fontSize="small" /> {t('lobby.settings.premiumSection')}
          </h3>

          <div className="settings-modal__branding">
            <h4 className="settings-modal__branding-title">
              <AppIcon name="palette" fontSize="small" /> {t('lobby.settings.branding.title')}
            </h4>
            <p className="settings-modal__hint">{t('lobby.settings.branding.subtitle')}</p>

            {!isPremium && (
              <div className="settings-modal__premium-cta">
                <button
                  type="button"
                  className="settings-modal__go-premium"
                  data-testid="settings-go-premium"
                  onClick={handleGoPremium}
                >
                  <AppIcon name="premium" fontSize="small" />
                  {t('lobby.settings.branding.goPremium')}
                </button>
                {onHavePremiumCode && (
                  <button
                    type="button"
                    className="settings-modal__have-code"
                    data-testid="settings-have-premium-code"
                    onClick={handleHavePremiumCode}
                  >
                    {t('lobby.roomSetup.haveCode')}
                  </button>
                )}
              </div>
            )}

            <div className="settings-modal__branding-lock-wrap">
              <fieldset className="settings-modal__branding-fields" disabled={!brandingEditable}>
                <div className="settings-modal__field">
                  <label className="settings-modal__label">{t('lobby.settings.branding.colors')}</label>
                  <p className="settings-modal__hint settings-modal__hint--field">
                    {t('lobby.settings.branding.colorsHint')}
                  </p>
                  <div className="settings-modal__swatches">
                    {colorSwatches.map((swatch, index) => (
                      <div key={swatch.id} className="settings-modal__swatch">
                        {/*
                          Uncontrolled color input (defaultValue + stable id).
                          Do not pass `value` or call setState on every change — that closes
                          Chromium's native hex/RGB picker while typing.
                        */}
                        <input
                          type="color"
                          defaultValue={swatch.value}
                          aria-label={t('lobby.settings.branding.colorN', { n: index + 1 })}
                          onFocus={() => {
                            colorPickerOpenRef.current = true;
                          }}
                          onBlur={() => {
                            commitColorSwatchesFromRefs();
                            // Picker dismissal blurs after the outside click; delay so overlay can ignore it.
                            window.setTimeout(() => {
                              colorPickerOpenRef.current = false;
                            }, 0);
                          }}
                          onChange={(e) => {
                            colorValuesRef.current[swatch.id] = e.target.value.toUpperCase();
                          }}
                        />
                        <button
                          type="button"
                          className="settings-modal__swatch-remove"
                          aria-label={t('lobby.settings.branding.removeColor')}
                          onClick={() => {
                            const next = colorSwatches.filter((_, i) => i !== index);
                            replaceColorSwatches(next);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {colorSwatches.length < MAX_COLORS && (
                      <button
                        type="button"
                        className="settings-modal__add-color"
                        onClick={() => {
                          replaceColorSwatches([
                            ...colorSwatches.map((s) => ({
                              ...s,
                              value: colorValuesRef.current[s.id] ?? s.value,
                            })),
                            { id: makeColorSwatchId(), value: DEFAULT_SWATCH },
                          ]);
                        }}
                      >
                        + {t('lobby.settings.branding.addColor')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="settings-modal__field">
                  <div className="settings-modal__media-grid">
                    <div className="settings-modal__media-card">
                      <label className="settings-modal__label">{t('lobby.settings.branding.logo')}</label>
                      <div className="settings-modal__logo-row">
                        {logoDataUrl ? (
                          <img src={logoDataUrl} alt="" className="settings-modal__logo-preview" />
                        ) : (
                          <div className="settings-modal__logo-placeholder">
                            <AppIcon name="photoCamera" />
                          </div>
                        )}
                        <div className="settings-modal__logo-actions">
                          <button
                            type="button"
                            className="settings-modal__secondary-btn"
                            disabled={!brandingEditable || logoBusy}
                            onClick={() => logoInputRef.current?.click()}
                          >
                            {logoBusy ? t('common.processing') : t('lobby.settings.branding.uploadLogo')}
                          </button>
                          {logoDataUrl && (
                            <button
                              type="button"
                              className="settings-modal__text-btn"
                              disabled={!brandingEditable}
                              onClick={() => setLogoDataUrl(undefined)}
                            >
                              {t('lobby.settings.branding.removeLogo')}
                            </button>
                          )}
                        </div>
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          hidden
                          disabled={!brandingEditable}
                          onChange={(e) => handleLogoPick(e.target.files?.[0] ?? null)}
                        />
                      </div>
                      {logoError && <p className="settings-modal__error">{logoError}</p>}
                    </div>

                    <div className="settings-modal__media-card">
                      <label className="settings-modal__label">
                        {t('lobby.settings.branding.background')}
                      </label>
                      <div className="settings-modal__logo-row">
                        {backgroundDataUrl ? (
                          <img
                            src={backgroundDataUrl}
                            alt=""
                            className="settings-modal__logo-preview settings-modal__logo-preview--bg"
                          />
                        ) : (
                          <div className="settings-modal__logo-placeholder settings-modal__logo-preview--bg">
                            <AppIcon name="photoCamera" />
                          </div>
                        )}
                        <div className="settings-modal__logo-actions">
                          <button
                            type="button"
                            className="settings-modal__secondary-btn"
                            disabled={!brandingEditable || backgroundBusy}
                            onClick={() => backgroundInputRef.current?.click()}
                          >
                            {backgroundBusy
                              ? t('common.processing')
                              : t('lobby.settings.branding.uploadBackground')}
                          </button>
                          {backgroundDataUrl && (
                            <button
                              type="button"
                              className="settings-modal__text-btn"
                              disabled={!brandingEditable}
                              onClick={() => setBackgroundDataUrl(undefined)}
                            >
                              {t('lobby.settings.branding.removeBackground')}
                            </button>
                          )}
                        </div>
                        <input
                          ref={backgroundInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          hidden
                          disabled={!brandingEditable}
                          onChange={(e) => handleBackgroundPick(e.target.files?.[0] ?? null)}
                        />
                      </div>
                      <p className="settings-modal__hint settings-modal__hint--field">
                        {t('lobby.settings.branding.backgroundHint')}
                      </p>
                      {backgroundError && <p className="settings-modal__error">{backgroundError}</p>}
                    </div>
                  </div>
                </div>

                <div className="settings-modal__field">
                  <label className="settings-modal__label" htmlFor="settings-display-name">
                    {t('lobby.settings.branding.displayName')}
                  </label>
                  <input
                    id="settings-display-name"
                    data-testid="settings-display-name"
                    className="settings-modal__input"
                    type="text"
                    maxLength={60}
                    value={displayName}
                    placeholder={t('lobby.settings.branding.displayNamePlaceholder')}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>

                <div className="settings-modal__field">
                  <label className="settings-modal__label" htmlFor="settings-website">
                    {t('lobby.settings.branding.website')}
                  </label>
                  <input
                    id="settings-website"
                    data-testid="settings-website"
                    className="settings-modal__input"
                    type="url"
                    maxLength={200}
                    value={website}
                    placeholder={t('lobby.settings.branding.websitePlaceholder')}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                  <label className="settings-modal__checkbox" htmlFor="settings-allow-crawl">
                    <input
                      id="settings-allow-crawl"
                      data-testid="settings-allow-website-crawl"
                      type="checkbox"
                      checked={allowWebsiteCrawl}
                      onChange={(e) => setAllowWebsiteCrawl(e.target.checked)}
                    />
                    <span>{t('lobby.settings.branding.allowWebsiteCrawl')}</span>
                  </label>
                  <p className="settings-modal__hint settings-modal__hint--field">
                    {t('lobby.settings.branding.websiteHint')}
                  </p>
                </div>

                <div
                  className="settings-modal__field settings-modal__field--last settings-modal__company-questions"
                  data-testid="settings-company-questions"
                >
                  <label className="settings-modal__label">
                    {t('lobby.settings.companyQuestions.title')}
                  </label>
                  <p className="settings-modal__hint settings-modal__hint--field">
                    {t('lobby.settings.companyQuestions.hint')}
                  </p>
                  <div className="settings-modal__prompts-row">
                    <p
                      className={`settings-modal__prompts-left${outOfPrompts ? ' settings-modal__prompts-left--empty' : ''}`}
                      data-testid="settings-ai-prompts-left"
                    >
                      {t('lobby.settings.companyQuestions.promptsLeft', {
                        remaining: remainingPrompts,
                        limit: aiPromptUsage.limit,
                      })}
                    </p>
                    <button
                      type="button"
                      className={`settings-modal__buy-prompts-btn${outOfPrompts ? ' settings-modal__buy-prompts-btn--emphasis' : ''}`}
                      data-testid="settings-buy-ai-prompts"
                      disabled={!brandingEditable || topUpBusy}
                      onClick={handleBuyMorePrompts}
                    >
                      {topUpBusy
                        ? t('common.processing')
                        : t('lobby.settings.companyQuestions.buyMore')}
                    </button>
                  </div>

                  {!hasQuestions && !outOfPrompts && (
                    <button
                      type="button"
                      className="settings-modal__secondary-btn settings-modal__generate-btn"
                      data-testid="settings-generate-questions"
                      disabled={!brandingEditable || aiGenerating || !canGenerateAi}
                      onClick={handleGenerateQuestions}
                    >
                      {aiGenerating
                        ? t('lobby.settings.companyQuestions.generating')
                        : t('lobby.settings.companyQuestions.generate')}
                    </button>
                  )}

                  {hasQuestions && (
                    <div className="settings-modal__company-questions-actions">
                      <div className="settings-modal__company-questions-actions-left">
                        <button
                          type="button"
                          className="settings-modal__text-btn"
                          data-testid="settings-view-questions"
                          onClick={() => {
                            if (editingQuestions) return;
                            setShowQuestionsPreview((v) => !v);
                          }}
                          disabled={editingQuestions}
                        >
                          {showQuestionsPreview
                            ? t('lobby.settings.companyQuestions.hideQuestions')
                            : t('lobby.settings.companyQuestions.viewQuestions')}
                        </button>
                        <button
                          type="button"
                          className="settings-modal__text-btn"
                          data-testid="settings-email-questions"
                          onClick={() => setShowEmailForm((v) => !v)}
                          disabled={editingQuestions}
                        >
                          {t('lobby.settings.companyQuestions.emailQuestions')}
                        </button>
                      </div>
                      <div className="settings-modal__company-questions-actions-right">
                        {editingQuestions ? (
                          <>
                            <button
                              type="button"
                              className="settings-modal__text-btn"
                              data-testid="settings-cancel-edit-questions"
                              disabled={questionsEditBusy}
                              onClick={cancelEditingQuestions}
                            >
                              {t('common.cancel')}
                            </button>
                            <button
                              type="button"
                              className="settings-modal__text-btn"
                              data-testid="settings-save-questions"
                              disabled={
                                !brandingEditable ||
                                questionsEditBusy ||
                                !questionsEditValidation.ok
                              }
                              onClick={handleSaveEditedQuestions}
                            >
                              {questionsEditBusy ? t('common.processing') : t('common.save')}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="settings-modal__text-btn"
                            data-testid="settings-edit-questions"
                            disabled={!brandingEditable || questionsEditBusy}
                            onClick={startEditingQuestions}
                          >
                            {t('lobby.settings.companyQuestions.editQuestions')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {showEmailForm && (
                    <div className="settings-modal__email-row">
                      <input
                        type="email"
                        className="settings-modal__input"
                        data-testid="settings-questions-email"
                        placeholder={t('lobby.settings.companyQuestions.emailPlaceholder')}
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                      />
                      <button
                        type="button"
                        className="settings-modal__secondary-btn"
                        data-testid="settings-send-questions-email"
                        disabled={emailBusy}
                        onClick={handleEmailQuestions}
                      >
                        {emailBusy
                          ? t('common.processing')
                          : t('lobby.settings.companyQuestions.sendEmail')}
                      </button>
                    </div>
                  )}

                  {emailSentTo && (
                    <p className="settings-modal__hint settings-modal__hint--field">
                      {t('lobby.settings.companyQuestions.emailSent', { email: emailSentTo })}
                    </p>
                  )}

                  {aiError && <p className="settings-modal__error">{aiError}</p>}

                  {showQuestionsPreview && hasQuestions && (
                    <div className="settings-modal__questions-preview-wrap">
                      {editingQuestions ? (
                        <>
                          <textarea
                            className={`settings-modal__questions-edit${
                              questionsEditFormatError ? ' is-invalid' : ''
                            }`}
                            data-testid="settings-questions-edit"
                            value={questionsEditText}
                            onChange={(e) => {
                              setQuestionsEditText(e.target.value);
                              setQuestionsEditDirty(true);
                            }}
                            spellCheck
                            aria-invalid={!!questionsEditFormatError}
                            aria-describedby={
                              questionsEditFormatError
                                ? 'settings-questions-edit-error'
                                : undefined
                            }
                            aria-label={t('lobby.settings.companyQuestions.editQuestions')}
                          />
                          {questionsEditFormatError ? (
                            <p
                              id="settings-questions-edit-error"
                              className="settings-modal__error settings-modal__questions-edit-error"
                              data-testid="settings-questions-edit-error"
                              role="alert"
                            >
                              {questionsEditFormatError}
                            </p>
                          ) : (
                            <p className="settings-modal__hint settings-modal__hint--field">
                              {t('lobby.settings.companyQuestions.editHint')}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <ul
                            className="settings-modal__questions-preview"
                            data-testid="settings-questions-preview"
                          >
                            {renderQuestionsList()}
                          </ul>
                          <button
                            type="button"
                            className="settings-modal__questions-fullscreen-btn"
                            data-testid="settings-questions-fullscreen"
                            aria-label={t('lobby.settings.companyQuestions.fullscreenQuestions')}
                            title={t('lobby.settings.companyQuestions.fullscreenQuestions')}
                            onClick={() => setQuestionsFullscreen(true)}
                          >
                            <AppIcon name="fullscreen" fontSize="small" />
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  <div
                    className="settings-modal__secondary-cta-row"
                    hidden={showCustomQuestionForm && !canGenerateMore}
                  >
                    {!showCustomQuestionForm ? (
                      <button
                        type="button"
                        className="settings-modal__text-btn"
                        data-testid="settings-show-custom-question"
                        onClick={() => setShowCustomQuestionForm(true)}
                      >
                        {t('lobby.settings.companyQuestions.addCustomCta')}
                      </button>
                    ) : null}
                    {canGenerateMore && !outOfPrompts ? (
                      <button
                        type="button"
                        className="settings-modal__text-btn"
                        data-testid="settings-generate-more-questions"
                        disabled={!brandingEditable || aiGenerating || !canGenerateAi}
                        onClick={handleGenerateQuestions}
                      >
                        {aiGenerating
                          ? t('lobby.settings.companyQuestions.generating')
                          : t('lobby.settings.companyQuestions.generateMore')}
                      </button>
                    ) : null}
                  </div>

                  {showCustomQuestionForm && (
                    <div className="settings-modal__custom-question" data-testid="settings-custom-question">
                      <div className="settings-modal__custom-question-header">
                        <label className="settings-modal__label" id="settings-custom-question-label">
                          {t('lobby.settings.companyQuestions.addCustom')}
                        </label>
                        <button
                          type="button"
                          className="settings-modal__custom-question-close"
                          data-testid="settings-close-custom-question"
                          onClick={() => setShowCustomQuestionForm(false)}
                          aria-label={t('common.close')}
                        >
                          <AppIcon name="close" fontSize="small" />
                        </button>
                      </div>
                      <p className="settings-modal__hint settings-modal__hint--field">
                        {t('lobby.settings.companyQuestions.addCustomHint')}
                      </p>
                      <input
                        className="settings-modal__input"
                        data-testid="settings-custom-question-text"
                        type="text"
                        maxLength={300}
                        value={customQuestion}
                        placeholder={t('lobby.settings.companyQuestions.customQuestionPlaceholder')}
                        onChange={(e) => setCustomQuestion(e.target.value)}
                      />
                      <div className="settings-modal__custom-options">
                        {customOptions.map((opt, index) => (
                          <label key={index} className="settings-modal__custom-option">
                            <input
                              type="radio"
                              name="settings-custom-correct"
                              checked={customCorrectIndex === index}
                              onChange={() => setCustomCorrectIndex(index)}
                              aria-label={t('lobby.settings.companyQuestions.markCorrect', {
                                n: index + 1,
                              })}
                            />
                            <input
                              className="settings-modal__input"
                              data-testid={`settings-custom-option-${index}`}
                              type="text"
                              maxLength={120}
                              value={opt}
                              placeholder={t('lobby.settings.companyQuestions.customOptionPlaceholder', {
                                n: index + 1,
                              })}
                              onChange={(e) => {
                                const next = [...customOptions];
                                next[index] = e.target.value;
                                setCustomOptions(next);
                              }}
                            />
                          </label>
                        ))}
                      </div>
                      <p className="settings-modal__hint settings-modal__hint--field">
                        {t('lobby.settings.companyQuestions.correctHint')}
                      </p>
                      <input
                        className="settings-modal__input settings-modal__custom-interesting"
                        data-testid="settings-custom-interesting"
                        type="text"
                        maxLength={400}
                        value={customInteresting}
                        placeholder={t('lobby.settings.companyQuestions.customInterestingPlaceholder')}
                        onChange={(e) => setCustomInteresting(e.target.value)}
                        aria-label={t('lobby.settings.companyQuestions.customInterestingLabel')}
                      />
                      <p className="settings-modal__hint settings-modal__hint--field">
                        {t('lobby.settings.companyQuestions.customInterestingHint')}
                      </p>
                      <button
                        type="button"
                        className="settings-modal__secondary-btn settings-modal__add-custom-btn"
                        data-testid="settings-add-custom-question"
                        disabled={!brandingEditable || customBusy}
                        onClick={handleAddCustomQuestion}
                      >
                        {customBusy
                          ? t('common.processing')
                          : t('lobby.settings.companyQuestions.addCustomButton')}
                      </button>
                    </div>
                  )}
                </div>
              </fieldset>

              {!brandingEditable && (
                <div className="settings-modal__branding-veil" aria-hidden="true">
                  <div className="settings-modal__branding-veil-badge">
                    <AppIcon name="lock" fontSize="small" />
                    <span>{t('lobby.settings.branding.lockedHint')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="settings-modal__actions">
          <button type="button" className="settings-modal__secondary-btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            data-testid="settings-save-button"
            className="settings-modal__primary-btn"
            onClick={handleSave}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
    {questionsFullscreen && hasQuestions && (
      <div
        className="settings-modal__questions-fullscreen-overlay"
        role="dialog"
        aria-modal="true"
        aria-label={t('lobby.settings.companyQuestions.fullscreenQuestions')}
        onClick={() => setQuestionsFullscreen(false)}
      >
        <div
          className="settings-modal__questions-fullscreen-panel"
          onClick={(e) => e.stopPropagation()}
          data-testid="settings-questions-fullscreen-panel"
        >
          <div className="settings-modal__questions-fullscreen-header">
            <h3 className="settings-modal__questions-fullscreen-title">
              {t('lobby.settings.companyQuestions.title')}
            </h3>
            <button
              type="button"
              className="settings-modal__questions-fullscreen-close"
              data-testid="settings-questions-fullscreen-close"
              aria-label={t('lobby.settings.companyQuestions.closeFullscreenQuestions')}
              onClick={() => setQuestionsFullscreen(false)}
            >
              <AppIcon name="close" fontSize="small" />
            </button>
          </div>
          <ul className="settings-modal__questions-fullscreen-list">
            {renderQuestionsList()}
          </ul>
        </div>
      </div>
    )}
    </>
  );
};

export default SettingsModal;
